import app from 'flarum/app';
import Switch from 'flarum/components/Switch';
import Button from 'flarum/components/Button';
import icon from 'flarum/helpers/icon';
import cleanFieldConfiguration from '../cleanFieldConfiguration';
import SelectWithDisabled from '../components/SelectWithDisabled';
import FormGroup from '../components/FormGroup';
import PermissionDropdown from '../components/PermissionDropdown';
import ValidationErrors from '../components/ValidationErrors';

/* global m */

const translationPrefix = 'kilowhat-formulaire.forum.template-editor.';

export default class Field {
    constructor({
                    config = {},
                    submission = {},
                    uploader = null,
                    onchange = () => {
                    },
                    onduplicate = () => {
                    },
                    ondelete = () => {
                    },
                    disabled = false,
                    validationErrors = {},
                    depth = 0,
                    expertMode = false,
                }) {
        this.config = config;
        this.submission = submission;
        this.uploader = uploader;
        this.onchange = onchange;
        this.onduplicate = onduplicate;
        this.ondelete = ondelete;
        this.disabled = disabled;
        this.validationErrors = validationErrors;
        this.depth = depth;
        this.expertMode = expertMode;

        // To ensure they have a unique key
        // Only run the logic when a config is passed. Otherwise there could be a loop with cleanFieldConfiguration
        if (Object.keys(this.config).length) {
            cleanFieldConfiguration(this.config);
        }
    }

    getConfig(key, defaultValue = '') {
        return this.config[key] || defaultValue;
    }

    uniqueFieldId() {
        return 'formulaireField' + this.getConfig('unique');
    }

    setConfig(key) {
        return value => {
            if (value === null || value === false || value === '') {
                delete this.config[key];
            } else {
                this.config[key] = value;
            }

            if (key === 'type') {
                cleanFieldConfiguration(this.config);
            }

            this.onchange();
        };
    }

    getSubmission(defaultValue = '', property = 'value') {
        const entry = this.submission[this.getConfig('key')];

        if (entry) {
            return entry[property];
        }

        return defaultValue;
    }

    setSubmission(value) {
        if (value === null || value === false || value === '' || value === []) {
            delete this.submission[this.getConfig('key')];
        } else {
            if (!this.submission[this.getConfig('key')]) {
                this.submission[this.getConfig('key')] = {};
            }

            this.submission[this.getConfig('key')].value = value;
        }

        // Remove validation errors for this field when edited
        const theKeyInValidationErrorTree = this.validationErrors[this.getConfig('key')];
        if (theKeyInValidationErrorTree) {
            delete theKeyInValidationErrorTree.errors;
        }

        this.onchange();
    }

    whiteListConfigProperties() {
        return [
            'title',
            'description',
            'required',
            'key',
            'fillGroupIds',
        ];
    }

    configure() {
        // abstract
    }

    configureView() {
        const validationErrorsForAllFieldsHere = [];

        Object.keys(this.validationErrors).forEach(key => {
            if (!this.validationErrors.hasOwnProperty(key)) {
                return;
            }

            // If there are errors for the template field itself, when we reach the key "errors" this test will be true
            if (Array.isArray(this.validationErrors[key])) {
                validationErrorsForAllFieldsHere.push(...this.validationErrors[key]);
            }

            // If there are errors for individual properties of the field, they will be collected here
            if (this.validationErrors[key].errors) {
                validationErrorsForAllFieldsHere.push(...this.validationErrors[key].errors);
            }

            // This loop will skip "items" field sub-errors, because it will be an object inside an object
            // Which shouldn't have any key named errors (unless a sub-field has key "errors" of course)
        });

        const whitelistedProperties = this.whiteListConfigProperties();

        const typeOptions = {
            short: app.translator.trans(translationPrefix + 'type.short'),
            long: app.translator.trans(translationPrefix + 'type.long'),
            number: app.translator.trans(translationPrefix + 'type.number'),
            date: app.translator.trans(translationPrefix + 'type.date'),
            content: app.translator.trans(translationPrefix + 'type.content'),
        };

        if (this.depth === 0) {
            typeOptions.upload = app.translator.trans(translationPrefix + 'type.upload');
            typeOptions.items = app.translator.trans(translationPrefix + 'type.items');
        }

        return m('.FormulaireConfiguration', {
            key: this.getConfig('unique'),
            className: validationErrorsForAllFieldsHere.length ? 'in-error' : '',
        }, [
            m('.FormulaireConfiguration-handle.js-handle', icon('fas fa-grip-lines')),
            ValidationErrors.component({
                errors: validationErrorsForAllFieldsHere,
            }),
            m('.Form-group', [
                whitelistedProperties.indexOf('title') === -1 ? null : m('.FormulaireInputWithLabel', {
                    className: this.getConfig('title') ? 'FormulaireInputWithLabel--with-value' : '',
                }, [
                    m('input.FormControl', {
                        type: 'text',
                        value: this.getConfig('title'),
                        onchange: m.withAttr('value', this.setConfig('title')),
                        disabled: this.disabled,
                    }),
                    m('label', app.translator.trans(translationPrefix + 'title')),
                ]),
                SelectWithDisabled.component({
                    value: this.getConfig('type'),
                    options: typeOptions,
                    //TODO: shouldn't keep min/max, shouldn't keep key if there are existing submissions
                    onchange: this.setConfig('type'),
                    disabled: this.disabled,
                }),
            ]),
            whitelistedProperties.indexOf('description') === -1 ? null : m('.Form-group', m('.FormulaireInputWithLabel', {
                className: this.getConfig('description') ? 'FormulaireInputWithLabel--with-value' : '',
            }, [
                m('textarea.FormControl', {
                    value: this.getConfig('description'),
                    onchange: m.withAttr('value', this.setConfig('description')),
                    disabled: this.disabled,
                }),
                m('label', app.translator.trans(translationPrefix + 'description')),
            ])),
            this.configure(),
            this.expertMode && whitelistedProperties.indexOf('key') !== -1 ? [
                m('.Form-group', m('.FormulaireInputWithLabel', {
                    className: this.getConfig('key') ? 'FormulaireInputWithLabel--with-value' : '',
                }, [
                    m('input.FormControl', {
                        type: 'text',
                        value: this.getConfig('key'),
                        onchange: m.withAttr('value', this.setConfig('key')),
                        disabled: this.disabled,
                    }),
                    m('label', app.translator.trans(translationPrefix + 'key')),
                ])),
                // Only show permissions for fields with a key. Otherwise it means it can't be filled anyway
                m('.Form-group', [
                    m('label', app.translator.trans(translationPrefix + 'fill-permission')),
                    PermissionDropdown.component({
                        groupIds: this.getConfig('fillGroupIds', null),
                        onchange: this.setConfig('fillGroupIds'),
                    }),
                ]),
            ] : null,
            m('.Form-group', [
                whitelistedProperties.indexOf('required') === -1 ? null : Switch.component({
                    state: this.getConfig('required', false),
                    onchange: this.setConfig('required'),
                    disabled: this.disabled,
                }, app.translator.trans(translationPrefix + 'required')),
                ' ',
                Button.component({
                    onclick: this.onduplicate.bind(this),
                    className: 'Button Button--icon',
                    icon: 'fas fa-clone',
                    disabled: this.disabled,
                    title: app.translator.trans(translationPrefix + 'duplicate'),
                }),
                ' ',
                Button.component({
                    onclick: this.ondelete.bind(this),
                    className: 'Button Button--icon',
                    icon: 'fas fa-trash',
                    disabled: this.disabled,
                    title: app.translator.trans(translationPrefix + 'delete'),
                }),
            ]),
        ]);
    }

    fill() {
        // abstract
    }

    fillView() {
        const title = this.getConfig('title');
        const description = this.getConfig('description');
        const validationErrors = this.validationErrorsForThisField();

        //TODO: no background error color for "items" type (that would show for any child error)
        return FormGroup.component({
            validationErrors,
            label: m('label', {
                for: this.uniqueFieldId(),
            }, [
                title ? title : m('em', app.translator.trans(translationPrefix + 'missing-title')),
                this.getConfig('required', false) ? m('span.FormulaireRequired', ' *') : null,
            ]),
            description,
        }, this.fill());
    }

    display() {
        const submission = this.getSubmission();

        return m('p', submission ? submission : m('em', app.translator.trans(translationPrefix + 'no-answer')));
    }

    displayView() {
        const title = this.getConfig('title');

        return [
            m('h2', [
                title ? title : m('em', app.translator.trans(translationPrefix + 'missing-title')),
            ]),
            this.display(),
        ];
    }

    minMaxRules(type = 'number') {
        return m('.Form-group', [
            m('.FormulaireInputWithLabel', {
                className: this.getConfig('min') ? 'FormulaireInputWithLabel--with-value' : '',
            }, [
                m('input.FormControl', {
                    type,
                    step: type === 'number' ? 1 : undefined,
                    min: type === 'number' ? 0 : undefined,
                    value: this.getConfig('min'),
                    onchange: m.withAttr('value', value => {
                        if (type === 'number' && value) {
                            value = parseInt(value);
                        }

                        this.setConfig('min')(value);
                    }),
                    disabled: this.disabled,
                }),
                m('label', app.translator.trans(translationPrefix + 'min')),
            ]),
            m('.FormulaireInputWithLabel', {
                className: this.getConfig('max') ? 'FormulaireInputWithLabel--with-value' : '',
            }, [
                m('input.FormControl', {
                    type,
                    step: type === 'number' ? 1 : undefined,
                    min: type === 'number' ? 0 : undefined,
                    value: this.getConfig('max'),
                    onchange: m.withAttr('value', value => {
                        if (type === 'number' && value) {
                            value = parseInt(value);
                        }

                        this.setConfig('max')(value);
                    }),
                    disabled: this.disabled,
                }),
                m('label', app.translator.trans(translationPrefix + 'max')),
            ]),
        ]);
    }

    isFillDisabled() {
        return this.disabled || this.getConfig('cannotFill');
    }

    validationErrorsForThisField() {
        const theKeyInValidationErrorTree = this.validationErrors[this.getConfig('key')];

        return theKeyInValidationErrorTree ? theKeyInValidationErrorTree.errors : null;
    }
}
